
import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";
import agent1Img from "../../assets/Agent2.png"
import agent2Img from "../../assets/Agent3.png"
import agent4Img from "../../assets/Agent6.png"

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderJavaAgent() {
  return <JavaComponent />;
}

function JavaComponent() {
  const navigate = useNavigate();
  return (
<div>
<main className="transition-opacity duration-300">
<div className="py-4">

{/* Breadcrumb */}
<nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
  <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
  <FiChevronRight />
  <span>Agent Setup Guide</span>
  <FiChevronRight />
  <span>Technology Agent</span>
  <FiChevronRight />
  <span className="text-[#1b140d] dark:text-white font-medium">Java</span>
</nav>

{/* Intro */}
<div className="mb-10 scroll-mt-24" id="introo">
  <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
    Documentation
  </span>

  <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
    <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
      Netraa APM
    </span>{" "}
    Java Agent
  </h1>

  <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
    Netraa Java Agent enables distributed tracing, JVM metrics collection,
    and log monitoring for Java-based applications including Spring Boot,
    Tomcat, and microservices architectures.
  </p>
</div>

{/* Prerequisites */}
<section className="mb-12 scroll-mt-24" id="prerequisites">
<h2 className="text-2xl font-bold text-text-main dark:text-white mb-6">
Prerequisites
</h2>

<div className="overflow-x-auto border border-border-color dark:border-border-dark rounded-xl">
<table className="w-full text-sm">
<thead className="bg-gray-100 dark:bg-white/5 text-left">
<tr>
<th className="px-4 py-3">Component</th>
<th className="px-4 py-3">Requirement</th>
</tr>
</thead>

<tbody>
<tr className="border-t">
<td className="px-4 py-3">Java Version</td>
<td className="px-4 py-3">Java 8 or higher</td>
</tr>

<tr className="border-t">
<td className="px-4 py-3">Application Type</td>
<td className="px-4 py-3">Spring Boot, Tomcat, JBoss, Standalone JAR, Glassfish</td>
</tr>

<tr className="border-t">
<td className="px-4 py-3">Network Access</td>
<td className="px-4 py-3">Connectivity to Netraa AOP (Port 11800)</td>
</tr>
</tbody>
</table>
</div>
</section>

{/* Step 1 */}
<section className="mb-12 scroll-mt-24" id="steps to follow">
<h2 className="text-2xl font-bold mb-4">Step 1 — Download & Extract Agent</h2>

<p className="text-sm mb-3">
Download the below files from the provided ApMoSys share point and place them in the project directory / repository
</p>
<ul className="list-disc pl-6 text-sm text-text-muted space-y-2 mb-3">
<li>apm-toolkit-logback-1.x-9.4.0.jar</li>
<li>apmosys-agent.zip</li>
<li>lagback.xml</li>
</ul>

<CodeBlock code={`unzip apmosys-agent.zip`} />

<p className="text-sm mt-4 mb-2">Extract to desired location:</p>

<CodeBlock code={`unzip apmosys-agent.zip -d /opt/netraa/agent/`} />
</section>

{/* Step 2 */}
<section className="mb-12">
<h2 className="text-2xl font-bold mb-4">Step 2 — Configure Agent</h2>

<p className="text-sm mb-3">
Navigate to the agent configuration directory and update the configuration file.
</p>

<CodeBlock code={`cd apmosys-agent/config`} />

<p className="text-sm mt-4 mb-2">Update the following properties:</p>

<CodeBlock code={`agent.service_name=Your_Service_Name
collector.backend_service=IP:11800`} />

<div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mt-6 rounded-r-lg">
<p className="text-xs">
Ensure <strong>service_name</strong> is unique across all applications.
</p>
</div>

<p className="text-sm mt-4">
If the service runs on multiple hosts, specify host IP manually,
otherwise a UUID will be auto-generated.
</p>

<div className="w-full mt-4">
  <img src={agent1Img} className="w-full object-contain" />
</div>

<div className="w-full mt-4">
  <img src={agent2Img} className="w-full object-contain" />
</div>

</section>

{/* Step 3 */}
{/* <section className="mb-12">
<h2 className="text-2xl font-bold mb-4">Step 3 — Attach Agent to JVM</h2>

<p className="text-sm mb-3">
Add the Java agent while starting your application.
</p>

<CodeBlock code={`java -javaagent:/path/to/netraa-agent.jar -jar application.jar`} />
</section> */}

{/* Step 4 */}
<section className="mb-12">
<h2 className="text-2xl font-bold mb-4">Step 3 — Platform Specific Setup</h2>

<div className="grid grid-cols-1 md:grid-cols-1 gap-6">

<div className="border rounded-xl p-4">
<h4 className="font-bold text-sm mb-2">Linux Tomcat</h4>
<CodeBlock code={`export CATALINA_OPTS="$CATALINA_OPTS -javaagent:/path/to/apmosys-agent.jar"`} />
</div>

<div className="border rounded-xl p-4">
<h4 className="font-bold text-sm mb-2">Windows Tomcat</h4>
<CodeBlock code={`set CATALINA_OPTS=%CATALINA_OPTS% -javaagent:C:\\path\\to\\agent.jar`} />
</div>

<div className="border rounded-xl p-4">
<h4 className="font-bold text-sm mb-2">Standalone JAR</h4>
<CodeBlock code={`java -javaagent:/path/to/apmosys-agent.jar -jar app.jar`} />
</div>

<div className="border rounded-xl p-4 space-y-4">
  <h4 className="font-bold text-sm">JBoss / WildFly Deployment</h4>

  {/* Linux */}
  <div>
    <p className="text-xs mb-2 font-semibold">Linux – standalone.conf</p>
    <CodeBlock
      code={`# Edit file
nano $JBOSS_HOME/bin/standalone.conf

# Add near JAVA_OPTS or at top
JAVA_OPTS="$JAVA_OPTS -Dsun.util.logging.disableCallerCheck=true \\
-Xbootclasspath/a:$JBOSS_HOME/modules/system/layers/base/org/wildfly/common/main/wildfly-common-x.x.x.Final.jar:\\
$JBOSS_HOME/modules/system/layers/base/org/jboss/logmanager/main/jboss-logmanager-x.x.x.Final.jar \\
-Djava.util.logging.manager=org.jboss.logmanager.LogManager \\
-javaagent:/path/to/apmosys-agent/netraa-agent.jar \\
-Djboss.modules.system.pkgs=org.jboss.byteman,org.jboss.logmanager,com.apmosys"`}
    />
  </div>

  {/* Windows */}
  <div>
    <p className="text-xs mb-2 font-semibold">Windows – standalone.conf.bat</p>
    <CodeBlock
      code={`REM Edit file
notepad %JBOSS_HOME%\\bin\\standalone.conf.bat

REM Add:
set "JAVA_OPTS=%JAVA_OPTS% -Dsun.util.logging.disableCallerCheck=true ^
-Xbootclasspath/a:%JBOSS_HOME%\\modules\\system\\layers\\base\\org\\wildfly\\common\\main\\wildfly-common-x.x.x.Final.jar;^
%JBOSS_HOME%\\modules\\system\\layers\\base\\org\\jboss\\logmanager\\main\\jboss-logmanager-x.x.x.Final.jar ^
-Djava.util.logging.manager=org.jboss.logmanager.LogManager ^
-javaagent:C:\\path\\to\\apmosys-agent\\netraa-agent.jar ^
-Djboss.modules.system.pkgs=org.jboss.byteman,org.jboss.logmanager,com.apmosys"`}
    />
  </div>

  {/* Version Info */}
  <div>
    <p className="text-xs mb-2 font-semibold">Version Selection</p>
    <CodeBlock
      code={`# Replace x.x.x with actual versions from your installation

# Example (WildFly 18):
wildfly-common-1.5.1.Final.jar
jboss-logmanager-2.1.14.Final.jar

# Verify versions:
ls $JBOSS_HOME/modules/system/layers/base/org/wildfly/common/main/
ls $JBOSS_HOME/modules/system/layers/base/org/jboss/logmanager/main/`}
    />
  </div>

</div>

{/* WebSphere */}
  <div className="border rounded-xl p-4">

    <h4 className="font-bold text-sm mb-2">IBM WebSphere Deployment</h4>
    <CodeBlock
      code={`# Login to Admin Console
https://<host>:9043/ibm/console

# Navigate:
Servers → Server Types → WebSphere application servers → server1
→ Java and Process Management → Process Definition → Java Virtual Machine

# Add in Generic JVM Arguments:
-javaagent:/path/to/apmosys-agent/netraa-agent.jar

# Apply and Save changes, then restart the server.`}
    />
  </div>

<div className="border rounded-xl p-4 space-y-3">
  <h4 className="font-bold text-sm mb-2">GlassFish Deployment</h4>

  <p className="text-sm">
    Log in to the GlassFish Admin Console using your admin credentials.
  </p>

  <p className="text-sm">
    Navigate to: Configurations → server-config → JVM Settings → JVM Options.
  </p>

  <p className="text-sm">
    Click "Add JVM Option" (or "New") and add the following:
  </p>

  <CodeBlock
    code={`-javaagent:/path/to/java/agent/netraa-agent.jar
-Dnetraa.agent.service_name=glassfish-server
-Dnetraa.agent.instance_name=glassfish-instance
-Dnetraa.collector.backend_service=192.168.x.x:11800`}
  />

  <p className="text-sm">
    Save the changes and restart the domain.
  </p>

  <CodeBlock
    code={`asadmin restart-domain domain1

# OR

asadmin stop-domain domain1
asadmin start-domain domain1`}
  />

  <p className="text-sm">
    Verify the agent is loaded:
  </p>

  <CodeBlock
    code={`ps -ef | grep GlassFishMain

# OR

ps -ef | grep java | grep javaagent`}
  />
</div>

<div className="border rounded-xl p-4">
<h4 className="font-bold text-sm mb-2">Docker</h4>
<p className="mb-2"> Place the agent file in the root project directory and in the docker file paste the below changes:</p>
<CodeBlock code={`COPY apmosys-agent /app/apmosys-agent
ENTRYPOINT ["java","-javaagent:/app/apmosys-agent/netraa-agent.jar","-jar","project-execution-service.jar"]`} />
</div>
</div>
</section>

{/* Step 5 */}
{/* <section className="mb-12">
<h2 className="text-2xl font-bold mb-4">Step 5 — Netraa UI Configuration</h2>

<p className="text-sm mb-3">
Navigate in Netraa UI:
</p>

<CodeBlock code={`Configuration > Admin-Config > Application & IP Mapping`} />

<ul className="list-disc pl-5 text-sm text-text-muted mt-3 space-y-1">
<li>Add application name</li>
<li>Select Application Type: APM Service</li>
<li>Provide host IP</li>
<li>Select environment</li>
</ul>

<div className="w-full mt-4">
  <img src={agent4Img} className="w-full object-contain" />
</div>
</section> */}

{/* Step 6 */}
{/* <section className="mb-12">
<h2 className="text-2xl font-bold mb-4">Step 6 — Verify Java Version</h2>

<CodeBlock code={`java -version
echo $JAVA_HOME`} />
</section> */}

{/* Java Agent Logs Monitoring */}
<section className="mb-8 scroll-mt-24" id="logs monitoring">

  {/* Title */}
  <h2 className="text-2xl font-bold mb-6">
    Java Agent Logs Monitoring
  </h2>

  {/* STEP 1 */}
  <div className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent">
    <h3 className="font-semibold text-sm mb-3">
      Step 1: Install Dependency into Local Maven Repo
    </h3>
    <CodeBlock code={`mvn install:install-file -Dfile=apm-toolkit-logback-1.x-9.4.0.jar -DgroupId=com.apmosys -DartifactId=apm-toolkit-logback-1.x -Dversion=9.4.0 -Dpackaging=jar`} />
  </div>

  {/* STEP 2 */}
  <div className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent">
    <h3 className="font-semibold text-sm mb-3">
      Step 2: Add Dependency to Application pom.xml
    </h3>
    <CodeBlock code={`<dependency>
  <groupId>com.apmosys</groupId>
  <artifactId>apm-toolkit-logback-1.x</artifactId>
  <version>9.4.0</version>
</dependency>`} />
  </div>

  {/* STEP 3 */}
  <div className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent">
    <h3 className="font-semibold text-sm mb-3">
      Step 3: Configure Logback
    </h3>

    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
      Ensure your application is using <span className="font-medium">Logback</span> and that the configuration file 
      (<code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">logback.xml</code> or{" "}
      <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">logback-spring.xml</code>) 
      is present on the classpath.
    </p>

    <p className="text-sm mb-3 text-gray-600 dark:text-gray-300 leading-relaxed">
      If you already have a <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">logback.xml</code>, 
      just add the required appender. Otherwise, you can use the sample configuration below.
    </p>

    <CodeBlock code={`<?xml version="1.0" encoding="UTF-8"?>

<configuration>

    <appender name="stdout" class="ch.qos.logback.core.ConsoleAppender">
        <encoder>
            <Pattern>%d{yyyy-MM-dd HH:mm:ss.SSS} - [%thread] - %-5level - %class{0} - %method - %msg%n</Pattern>
        </encoder>
    </appender>
    <appender name="grpc-log"
              class="com.apmosys.apm.toolkit.log.logback.v1.x.log.GRPCLogClientAppender">
        <encoder class="ch.qos.logback.core.encoder.LayoutWrappingEncoder">
            <layout
                    class="com.apmosys.apm.toolkit.log.logback.v1.x.mdc.TraceIdMDCPatternLogbackLayout">
                <Pattern>%d{yyyy-MM-dd HH:mm:ss.SSS} [%thread] %-5level %logger{36} -%msg%n</Pattern>
            </layout>
        </encoder>
    </appender>

    <root level="INFO">
        <appender-ref ref="grpc-log" />
    </root>

    <root level="INFO">
        <appender-ref ref="stdout" />
    </root>
</configuration>`} />
  </div>

</section>

{/* Recommended */}
<section className="mb-10">
<h2 className="text-2xl font-bold mb-4">Recommended Configuration</h2>

<ul className="list-disc pl-6 text-sm text-text-muted space-y-2">
<li>Always define a meaningful service name</li>
<li>Ensure backend connectivity to AOP</li>
<li>Use consistent naming across environments</li>
<li>Restart application after config changes</li>
</ul>
</section>

{/* Visualize Data CTA */}
<section className="mb-10 flex justify-center">
  <button
    onClick={() => navigate("/homeservices")}
    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white
               bg-gradient-to-r from-orange-500 to-red-600
               hover:from-orange-600 hover:to-red-700
               shadow-md hover:shadow-lg transition-all duration-300
               dark:shadow-orange-900/30"
  >
    Visualize Data
    <FiChevronRight className="w-4 h-4" />
  </button>
</section>

</div>
</main>
</div>
);
    }